import {booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, Input, signal} from "@angular/core";
import {GenerellEpostTilMedlemmerSpesifikasjon, GruppeId, GruppeMedlemListeModell, GruppeRessursClient, Gruppetype, Medlemstatus, SendEpostResultatModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeLogoKomponent} from "../gruppe-logo/gruppe-logo";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatMenuModule} from "@angular/material/menu";
import {TranslateModule} from "@ngx-translate/core";
import {NgClass} from "@angular/common";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TidssoneKomponent} from "web-ng2/delt/komponenter/tidssone/tidssone";
import {LokaleKomponent} from "web-ng2/delt/komponenter/lokale/lokale";
import {MatTooltipModule} from "@angular/material/tooltip";
import {GruppeRedigerKomponent} from "../gruppe-rediger/gruppe-rediger";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {MedlemImporterDialogKomponent} from "web-ng2/moduler/medlem/importer-medlemmer/modale/medlem-importer-dialog/medlem-importer-dialog";
import {MatDialog} from "@angular/material/dialog";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {GruppeMøtedeltakelseeksportDialogKomponent} from "../../modale/gruppe-moetedeltakelseeksport-dialog/gruppe-moetedeltakelseeksport-dialog";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {filter, Observable, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import KreverTilgangsrolleGruppeDirektiv from "web-ng2/delt/direktiver/kreverTilgangsrolleGruppe/kreverTilgangsrolleGrupper";
import {tomGruppeModell} from "felles-ng2/modell/tommeModeller";
import {SendEpostDialogKomponent} from "web-ng2/moduler/epost/modale/send-epost-dialog/send-epost-dialog";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('GruppeVisDetaljerKomponent');

/**
 * Viser gruppe detaljer.
 */
@Component({
    selector: 'gruppe-vis-detaljer',
    templateUrl: 'gruppe-vis-detaljer.html',
    styleUrl: 'gruppe-vis-detaljer.scss',
    host: { 'data-testid': 'gruppe-vis-detaljer' },
    imports: [
        MatIcon,
        MatTooltipModule,
        MatIconButton,
        MatMenuModule,
        TranslateModule,
        NgClass,
        GruppeLogoKomponent,
        TittelVerdiSamlingKomponent,
        TittelVerdiKomponent,
        VerdiKomponent,
        TidssoneKomponent,
        LokaleKomponent,
        GruppeRedigerKomponent,
        KreverTilgangsrolleGruppeDirektiv,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeVisDetaljerKomponent {
    static ajsSelector = 'gruppeVisDetaljer';

    @Input({transform: booleanAttribute}) visRedigerMoetemalMenyinnslag = true;

    gruppe = signal<GruppeModellDML>(tomGruppeModell());
    medlemmer = signal<GruppeMedlemListeModell[]>([]);
    erMalgruppe = computed(()=>this.gruppe()?.gruppetype == Gruppetype.mal);
    erRediger = signal(false);

    constructor(private avhengigheter: GruppeVisDetaljerKomponentAvhengigheter,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private oversettelser: Oversettelser,
                private matDialog: MatDialog,
                private navigasjon:Navigasjon,
                private destroyRef:DestroyRef) {
        //hent medlemmer hver gang gruppe endres
        toObservable(this.gruppe).pipe(filter((gruppe: GruppeModellDML) => erIkkeTomStreng(gruppe.id)), switchMap(gruppe => this.avhengigheter.medlemmer(gruppe.id)), takeUntilDestroyed(this.destroyRef))
          .subscribe(medlemmer => this.medlemmer.set(medlemmer));
    }

    //kan ikke bruker input.required pga komponent brukt i ajs
    @Input({required: true}) set gruppeId(gruppeId: GruppeId) {
        if (erNil(gruppeId)) return;
        this.avhengigheter.hent(gruppeId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(gruppe => this.gruppe.set(gruppe));
    }

    rediger() {
        this.erRediger.set(true);
        this.navigasjonDeaktivering.deaktiverNavigasjon();
    }

    redigerFerdig(){
        this.erRediger.set(false);
        this.navigasjonDeaktivering.reaktiverNavigasjon();
    }

    importerMedlemmer() {
        MedlemImporterDialogKomponent.vis(this.matDialog, this.gruppe()!);
    }

    eksporterMoetedeltakelse() {
        GruppeMøtedeltakelseeksportDialogKomponent.vis(this.matDialog, this.gruppe()!.id);
    }

    redigerMoetemal() {
        this.navigasjon.gåTilGruppeMøtemalRediger(this.gruppe()!.id);
    }

    sendEpostDialog(){
        const valgbareMedlemmer = this.medlemmer()
          .filter(medlem => erIkkeNil(medlem.brukerId) && medlem.medlemstatus == Medlemstatus.normal_tilgang)
        SendEpostDialogKomponent.vis(this.matDialog, this.oversettelser.oversett(i18n('E-post til {gruppetittel} i {organisasjon}'),{gruppetittel:this.gruppe().tittel, organisasjon: this.gruppe().organisasjonsnavn}), valgbareMedlemmer, valgbareMedlemmer, this.sendEpost.bind(this))
    }

    // må matche signatur til metode i sendEpostDialog
    sendEpost(epostTilMedlemmerSpesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon): Promise<SendEpostResultatModell> {
        return this.avhengigheter.sendEpostTilGruppe(this.gruppe().id, epostTilMedlemmerSpesifikasjon);
    }

}

@Injectable()
export class GruppeVisDetaljerKomponentAvhengigheter {

    constructor(private gruppeDatatjeneste:GruppeDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste, private gruppeRessurs: GruppeRessursClient) {
    }

    hent(gruppeId: GruppeId):Observable<GruppeModellDML> {
        //så lenge denne komp blir brukt i ajs (møte.ts) lar vi
        //kaller være ansvarlig for at gruppe er tilgjengelig i datalager,
        //slik at vi slipper unødvendig relasting her..
        return this.gruppeDatatjeneste.hentFraLokallagerSomObserverbar(gruppeId);
    }

    medlemmer(id: GruppeId): Observable<GruppeMedlemListeModell[]> {
        //så lenge denne komp blir brukt i ajs (møte.ts) lar vi
        //kaller være ansvarlig for at gruppe er tilgjengelig i datalager,
        //slik at vi slipper unødvendig relasting her..
        return this.gruppemedlemDatatjeneste.finnTilknyttetFraLokallager(id)
    }

    sendEpostTilGruppe(gruppeId: GruppeId,spesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon): Promise<SendEpostResultatModell>{
        return this.gruppeRessurs.sendEpost(gruppeId, spesifikasjon).somPromise();
    }

}
